import { Component, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConsentFromPerseWalletComponent } from './consent-from-perse-wallet.component';

// Type definitions for consent data
interface CompanyDetails {
  companyNumber?: string;
  companyName?: string;
}

interface CustomerDetails {
  email: string;
  firstName?: string;
  lastName?: string;
  signUpType?: 'individual' | 'company';
  companyDetails?: CompanyDetails;
  phoneNumber?: string;
  role?: string;
}

interface Meter {
  mpxn: string;
  fuelType: 'Elec' | 'Gas';
  address?: string;
}

interface MeterDetails {
  meters: Meter[];
}

interface ConsentResult {
  consent: 'SUCCESS' | 'ERROR';
  value: string;
}

@Component({
  selector: 'app-consent-wallet-fc',
  standalone: true,
  imports: [
    CommonModule,
    ConsentFromPerseWalletComponent
  ],
  template: `
    <app-consent-from-perse-wallet
      [customerDetails]="customerDetails"
      [meterDetails]="meterDetails"
      [isRequestor]="isRequestor"
      (consentResult)="onConsentResult($event)">
    </app-consent-from-perse-wallet>
    
    @if (consentStatus === 'SUCCESS') {
      <div class="consent-success-message">
        @if (isRequestor) {
          ✅ Consent request sent successfully via email
        } @else {
          ✅ Consent successfully granted via Perse ConsentWallet
        }
      </div>
    }
    
    @if (consentStatus === 'ERROR') {
      <div class="consent-error-message">
        ❌ {{ isRequestor ? 'Consent request failed' : 'Consent failed' }}: {{ errorMessage }}
      </div>
    }
  `,
  styles: [`
    .consent-success-message {
      color: #4caf50;
      margin-top: 16px;
      padding: 12px;
      background-color: #e8f5e8;
      border-radius: 4px;
      border: 1px solid #4caf50;
    }
    
    .consent-error-message {
      color: #f44336;
      margin-top: 16px;
      padding: 12px;
      background-color: #ffeaea;
      border-radius: 4px;
      border: 1px solid #f44336;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ConsentWalletFcComponent),
      multi: true
    }
  ]
})
export class ConsentWalletFcComponent implements ControlValueAccessor {
  
  @Input() customerDetails?: CustomerDetails;
  @Input() meterDetails?: MeterDetails;
  @Input() isRequestor: boolean = false; // Pass-through for TPI scenarios

  private value: boolean = false;
  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  consentStatus: 'PENDING' | 'SUCCESS' | 'ERROR' = 'PENDING';
  errorMessage: string = '';

  constructor() { }

  /**
   * Handles the consent result from the Perse wallet component
   */
  onConsentResult(result: ConsentResult): void {
    console.log('Consent result received:', result);
    
    this.consentStatus = result.consent;
    
    if (result.consent === 'SUCCESS') {
      this.value = true;
      this.errorMessage = '';
    } else {
      this.value = false;
      this.errorMessage = this.getErrorMessage(result.value);
    }
    
    // Notify the form control of the change
    this.onChange(this.value);
    this.onTouched();
  }

  /**
   * Converts error codes to user-friendly messages
   */
  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'REQUEST_CANCELLED':
        return this.isRequestor ? 'Email request was cancelled' : 'Consent request was cancelled by user';
      case 'INVALID_CUSTOMER_DETAILS':
        return 'Invalid customer details provided';
      case 'INVALID_METER_DETAILS':
        return 'Invalid meter details provided';
      case 'UNAUTHORIZED':
        return 'Authorisation failed - please try again';
      default:
        return `An error occurred: ${errorCode}`;
    }
  }

  // ControlValueAccessor implementation
  writeValue(value: boolean): void {
    this.value = value;
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // Implementation for disabled state if needed
  }
}
